import { AddCircleIcon, CoinPlusIcon } from '@synergy/theme-next';
import { ArrayHelpers, Field, FieldArray, FieldProps, FormikProps } from 'formik';
import { Box, Stack } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';
import {
    PALO_QUANTITY_HEAD_SX,
    PaloDirtyCell,
    PaloOverflowAction,
    PaloPositionColumn,
    PaloPositionTable,
    PaloQuantityCell,
    paloCurrencySign,
    paloHasDescription,
} from '@synergy/components-palo';
import { PaloNextThemeProvider, tokens } from '@synergy/theme-palo';
import { PoolButton, PoolNumberTextfield, PoolSplitButton } from '@synergy/components-next';
import { ProductDetails } from '@synergy/components';
import { ReactElement, useState } from 'react';
import { UserRole } from '@synergy/models';
import { getLocalizedString, hasRole } from '@synergy/utils';

import { CustomPosition } from '../../../common/components/free-position-dialog/model/custom-position';
import { CustomPositionFields } from '../../../common/components/free-position-dialog/model/custom-position-fields';
import { FinancialCalculations } from '../../../common/utils/financial-calculations';
import { FreePositionDialog } from '../../../common/components/free-position-dialog/free-position-dialog';
import { PaloAddPositionsDialog } from '../../../common/components/palo/palo-add-positions-dialog';
import { PaloPositionText } from '../../../common/components/palo/palo-position-text';
import { PaloRowProductSearch } from '../../../common/components/palo/palo-row-product-search';
import { PaloSupplierBonusBadge } from '../../../common/components/palo/palo-supplier-bonus-badge';
import { Product } from '../../../common/models/product';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { SalesOrder } from '../models/salesOrder';
import { SalesOrderPositionItem } from '../models/salesOrder-position-item';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useDocumentChain } from '../../document-chain/palo-document-chain-context';

export interface SalesOrderDetailPositionTableProps {
    positionIndex: number;
    focusAfterMount?: boolean;
    readonly?: boolean;
}

interface CustomData {
    id?: number;
    index?: number;
}

const c = tokens.color;

/**
 * The purchase order made for the supplier of a row, from the chain the
 * "Belege" tab loads anyway (#2160). The chain names an order's supplier, not
 * the row it came from: the supplier is what ties them (as in #2256).
 */
const RowFollowUp = ({ supplierName }: { supplierName?: string }): ReactElement => {
    const { steps } = useDocumentChain();
    const orders = supplierName
        ? steps.filter(step => 'purchase-orders' === step.type && step.supplier === supplierName)
        : [];

    if (0 === orders.length) {
        return (
            <Box component="span" sx={{ color: c.ink3 }}>
                –
            </Box>
        );
    }

    return (
        <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            {orders.map(order => (
                <Link
                    key={order.key}
                    to={ROUTES.purchaseOrdersDetail.replace(':id', order.id.toString())}
                    style={{ color: c.accentHi, textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                    <FormattedMessage id="app.document.purchaseOrder" /> {order.number ?? `#${order.id}`}
                </Link>
            ))}
        </Box>
    );
};

/** A row that carries no article and no name of its own waits for one. */
const isBlank = (item: SalesOrderPositionItem): boolean =>
    !item.product && 0 === Object.values(item.name ?? {}).filter(Boolean).length;

/**
 * The articles one member asks for, in the table every document shares.
 *
 * It used to be a table of the house's with a search line at its foot and a
 * row of icon buttons per position. The order holds the supplier and the shop
 * price of every article, so the table shows them, read-only, with what each
 * row comes to and the order it went into (#2259).
 */
export const SalesOrderDetailPositionTable = ({
    positionIndex,
    readonly = false,
}: SalesOrderDetailPositionTableProps): ReactElement => {
    const user = useAppSelector(state => state.user);
    const locale = useAppSelector(bestLocaleSelector);
    const appLocale = useAppSelector(state => state.currentLocale);
    const currency = useAppSelector(state => state.settings.currency);
    const [showFreePosition, setShowFreePosition] = useState(false);
    const [showAddMultiple, setShowAddMultiple] = useState(false);
    const [customPosition, setCustomPosition] = useState<CustomPosition | undefined>(undefined);
    const [customData, setCustomData] = useState<CustomData | undefined>(undefined);
    const mayFollowLink = hasRole([UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales], user);
    const currencySign = paloCurrencySign(currency);
    const fieldName = `positions.${positionIndex}.items`;

    const itemsOf = (helper: ArrayHelpers & { form: FormikProps<SalesOrder> }): SalesOrderPositionItem[] => [
        ...helper.form.values.positions[positionIndex].items,
    ];

    const writeItems = (
        helper: ArrayHelpers & { form: FormikProps<SalesOrder> },
        items: SalesOrderPositionItem[]
    ): void => {
        helper.form.setFieldValue(fieldName, items);
    };

    const showFreePositionDialog = (index?: number, position?: SalesOrderPositionItem): void => {
        setCustomPosition(position);
        setCustomData(position && index !== undefined ? { id: position.id, index } : undefined);
        setShowFreePosition(true);
    };

    const buildColumns = (
        helper: ArrayHelpers & { form: FormikProps<SalesOrder> },
        priced: boolean
    ): PaloPositionColumn<SalesOrderPositionItem>[] => [
        {
            key: 'name',
            type: 'name',
            label: <FormattedMessage id="app.table.column.article" />,
            render: (item, index) =>
                isBlank(item) && !readonly ? (
                    <PaloRowProductSearch
                        onSelected={product => {
                            // Keeps its place in the list; only what it is
                            // about arrives.
                            const next = itemsOf(helper);
                            next[index] = SalesOrderPositionItem.fromPlainObject(
                                SalesOrderPositionItem.fromProduct(product, locale)
                            );
                            writeItems(helper, next);
                        }}
                        includeNormArticles={true}
                    />
                ) : (
                    <>
                        <ProductDetails
                            product={item}
                            showDescription={false}
                            inlineNumber={true}
                            url={
                                mayFollowLink && item.product
                                    ? ROUTES.products.product.detail.replace(':id', item.product.id.toString())
                                    : undefined
                            }
                        />
                        <PaloPositionText
                            index={index}
                            shortDescription={item.shortDescription}
                            longDescription={item.longDescription}
                            // Only a free article has a dialog of its own.
                            onEdit={!readonly && !item.product ? () => showFreePositionDialog(index, item) : undefined}
                        />
                    </>
                ),
        },
        {
            key: 'supplier',
            type: 'name',
            width: '170px',
            label: <FormattedMessage id="app.table.column.supplier" />,
            render: item => (
                // The badge under the name: beside it, it took the room the
                // prices need and the table no longer fitted the mask.
                <Box
                    component="span"
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', minWidth: 0 }}
                >
                    <Box component="span" sx={{ minWidth: 0 }}>
                        {item.supplierAccountName ?? '–'}
                    </Box>
                    {item.supplierAccountId && (
                        <PaloSupplierBonusBadge
                            supplierId={item.supplierAccountId}
                            supplierName={item.supplierAccountName}
                        />
                    )}
                </Box>
            ),
        },
        {
            key: 'quantity',
            type: 'quantity',
            width: '130px',
            label: <FormattedMessage id="app.table.column.quantity" />,
            headSx: PALO_QUANTITY_HEAD_SX,
            render: (item, index) => (
                <PaloQuantityCell unit={item.orderUnit}>
                    <PaloDirtyCell name={`${fieldName}.${index}.quantity`}>
                        <Field name={`${fieldName}.${index}.quantity`}>
                            {({ field, meta }: FieldProps<number>) => (
                                <PoolNumberTextfield
                                    {...field}
                                    type="dense"
                                    readonly={readonly}
                                    textAlign="right"
                                    fullWidth={true}
                                    error={!!meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                />
                            )}
                        </Field>
                    </PaloDirtyCell>
                </PaloQuantityCell>
            ),
        },
        {
            key: 'price',
            type: 'amount',
            width: '96px',
            label: <FormattedMessage id="app.salesOrders.detail.column.price" values={{ value: currencySign }} />,
            // The shop's price when the member ordered; not changed here. A
            // single inquiry has none yet, which is a dash, not 0,00 (R-183).
            render: item =>
                !priced || null == item.price ? '–' : <FormattedNumber value={item.price} minimumFractionDigits={2} />,
        },
        {
            key: 'value',
            type: 'amount',
            width: '104px',
            label: <FormattedMessage id="app.salesOrders.detail.column.value" values={{ value: currencySign }} />,
            render: item =>
                !priced || null == item.price ? (
                    '–'
                ) : (
                    <FormattedNumber value={FinancialCalculations.getNetTotal(item)} minimumFractionDigits={2} />
                ),
        },
        {
            key: 'followUp',
            type: 'documentNumber',
            width: '124px',
            label: <FormattedMessage id="app.inquiries.detail.followUp" />,
            render: item => <RowFollowUp supplierName={item.supplierAccountName} />,
        },
    ];

    return (
        <FieldArray name={fieldName}>
            {helper => {
                const typed = helper as ArrayHelpers & { form: FormikProps<SalesOrder> };
                const items = typed.form.values.positions[positionIndex].items;
                // As the list decides: without a price above 0 there is no value yet.
                const priced = items.some(item => !!item.price);

                const rowActions = (item: SalesOrderPositionItem, index: number): PaloOverflowAction[] => {
                    if (readonly) {
                        return [];
                    }

                    return [
                        ...(item.product
                            ? []
                            : [
                                  {
                                      label: <FormattedMessage id="app.common.edit" />,
                                      onClick: () => showFreePositionDialog(index, item),
                                  },
                                  {
                                      label: <FormattedMessage id="app.common.duplicate" />,
                                      onClick: () => {
                                          const next = itemsOf(typed);
                                          next.splice(
                                              index + 1,
                                              0,
                                              SalesOrderPositionItem.fromPlainObject({ ...item, id: undefined })
                                          );
                                          writeItems(typed, next);
                                      },
                                  },
                              ]),
                        {
                            label: <FormattedMessage id="app.common.delete" />,
                            danger: true,
                            onClick: () => {
                                const next = itemsOf(typed);
                                next.splice(index, 1);
                                writeItems(typed, next);
                            },
                        },
                    ];
                };

                return (
                    <>
                        <PaloPositionTable
                            items={items}
                            columns={buildColumns(typed, priced)}
                            getRowKey={(item, index) => `${item.id ?? 'new'}-${index}`}
                            hasDescription={item =>
                                paloHasDescription(
                                    value => getLocalizedString([locale], value) ?? '',
                                    item.shortDescription,
                                    item.longDescription
                                )
                            }
                            readonly={readonly}
                            rowActions={rowActions}
                            quantityOf={item => item.quantity}
                            amountOf={priced ? item => FinancialCalculations.getNetTotal(item) : undefined}
                            currency={currency}
                            onMove={(from, to) => {
                                const next = itemsOf(typed);
                                const [moved] = next.splice(from, 1);
                                next.splice(to, 0, moved);
                                writeItems(typed, next);
                            }}
                            onDeleteMany={indices => {
                                // From the back, so the places of the ones still
                                // to go do not shift under the loop.
                                const next = itemsOf(typed);
                                [...indices].sort((left, right) => right - left).forEach(i => next.splice(i, 1));
                                writeItems(typed, next);
                            }}
                            onDuplicateMany={indices => {
                                const next = itemsOf(typed);
                                [...indices]
                                    .sort((left, right) => right - left)
                                    .forEach(index =>
                                        next.splice(
                                            index + 1,
                                            0,
                                            SalesOrderPositionItem.fromPlainObject({ ...next[index], id: undefined })
                                        )
                                    );
                                writeItems(typed, next);
                            }}
                            toolbar={
                                !readonly && (
                                    <Stack direction="row" gap={2} alignItems="flex-end">
                                        <PoolSplitButton
                                            variant="outlined"
                                            color="inherit"
                                            preSelectedIndex={0}
                                            menuItems={[
                                                {
                                                    label: <FormattedMessage id="app.positions.addRow" />,
                                                    displayLabel: <FormattedMessage id="app.positions.addRow" />,
                                                    icon: <AddCircleIcon />,
                                                    onClick: () =>
                                                        helper.push(
                                                            SalesOrderPositionItem.fromPlainObject({
                                                                name: {},
                                                                orderUnit: '',
                                                                quantity: 1,
                                                            })
                                                        ),
                                                },
                                                {
                                                    label: <FormattedMessage id="app.freePosition.addFreePosition" />,
                                                    displayLabel: (
                                                        <FormattedMessage id="app.freePosition.addFreePosition" />
                                                    ),
                                                    icon: <CoinPlusIcon />,
                                                    onClick: () => showFreePositionDialog(),
                                                },
                                            ]}
                                            sx={{ flexShrink: 0 }}
                                        />
                                        <PoolButton
                                            variant="outlined"
                                            color="inherit"
                                            startIcon={<AddCircleIcon />}
                                            onClick={() => setShowAddMultiple(true)}
                                        >
                                            <FormattedMessage id="app.positions.addMultiple" />
                                        </PoolButton>
                                        <PaloAddPositionsDialog
                                            open={showAddMultiple}
                                            onClose={() => setShowAddMultiple(false)}
                                            onSelect={(product: Product | null) =>
                                                product &&
                                                helper.push(
                                                    SalesOrderPositionItem.fromPlainObject(
                                                        SalesOrderPositionItem.fromProduct(product, locale)
                                                    )
                                                )
                                            }
                                            includeNormArticles={true}
                                        />
                                    </Stack>
                                )
                            }
                        />
                        <PaloNextThemeProvider>
                            <FreePositionDialog
                                customPosition={customPosition}
                                customData={customData}
                                isOpen={showFreePosition}
                                currencySymbol={currency}
                                locale={appLocale}
                                hideFields={[CustomPositionFields.Price, CustomPositionFields.Tax]}
                                requiredFields={[CustomPositionFields.OrderUnit, CustomPositionFields.Quantity]}
                                onCancel={() => setShowFreePosition(false)}
                                onSave={(value: CustomPosition, data?: unknown) => {
                                    const saved = data as CustomData;
                                    const item = SalesOrderPositionItem.fromPlainObject({
                                        ...value,
                                        id: saved?.id,
                                    });

                                    if (saved?.index !== undefined) {
                                        helper.replace(saved.index, item);
                                    } else {
                                        helper.push(item);
                                    }

                                    setShowFreePosition(false);
                                }}
                            />
                        </PaloNextThemeProvider>
                    </>
                );
            }}
        </FieldArray>
    );
};
